<template>
    <v-dialog :value="value" max-width="860" scrollable @input="$emit('input', $event)">
        <v-card class="doc-style-form">
            <v-card-title class="doc-style-form__title">
                {{ isNew ? "Novo estilo" : `Editar “${original.name}”` }}
                <v-spacer></v-spacer>
                <v-btn icon small aria-label="Fechar" @click="close"><v-icon size="20">mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text v-if="form" class="doc-style-form__body">
                <v-form ref="form" class="doc-style-form__fields" @submit.prevent="save">
                    <div class="doc-style-form__row">
                        <v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto" :rules="[(v) => !!(v || '').trim() || 'Informe o nome']" class="flex-grow-1" />
                        <v-select v-model="form.type" :items="types" label="Tipo" outlined dense hide-details :disabled="!isNew" style="max-width: 160px" />
                    </div>
                    <v-select v-if="form.type === 'paragraph'" v-model="form.element" :items="elements" label="Aplica como" outlined dense hide-details
                        :disabled="form.element === 'blockquote'" />

                    <v-alert v-if="fixedQuote" dense text type="info" class="mb-0">
                        A citação segue o padrão fixo do Guia de Formatação (itálico, recuo de 3 cm). Só o nome pode ser alterado.
                    </v-alert>

                    <fieldset class="doc-style-form__set" :disabled="fixedQuote">
                        <legend>Fonte</legend>
                        <div class="doc-style-form__row">
                            <v-select v-model="form.fontFamily" :items="fonts" label="Fonte" outlined dense hide-details class="flex-grow-1" />
                            <v-select v-model="form.fontSize" :items="sizes" label="Tamanho" outlined dense hide-details style="max-width: 130px" />
                        </div>
                        <div class="doc-style-form__toggles">
                            <v-btn-toggle v-model="marks" multiple dense>
                                <v-btn value="bold" small title="Negrito"><v-icon small>mdi-format-bold</v-icon></v-btn>
                                <v-btn value="italic" small title="Itálico"><v-icon small>mdi-format-italic</v-icon></v-btn>
                                <v-btn value="uppercase" small title="Maiúsculas"><v-icon small>mdi-format-letter-case-upper</v-icon></v-btn>
                            </v-btn-toggle>
                            <div class="doc-style-form__colors" role="radiogroup" aria-label="Cor">
                                <button type="button" class="doc-style-form__color" :class="{ 'doc-style-form__color--on': !form.color }" title="Automática"
                                    style="background: #111111" @click="form.color = null"></button>
                                <button v-for="color in colors" :key="color.bg" type="button" class="doc-style-form__color" :title="color.name"
                                    :class="{ 'doc-style-form__color--on': form.color === color.bg }" :style="{ background: color.bg }" @click="form.color = color.bg"></button>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="form.type === 'paragraph'" class="doc-style-form__set" :disabled="fixedQuote">
                        <legend>Parágrafo</legend>
                        <div class="doc-style-form__row">
                            <v-btn-toggle v-model="form.align" mandatory dense>
                                <v-btn v-for="align in aligns" :key="align.value" :value="align.value" small :title="align.label"><v-icon small>{{ align.icon }}</v-icon></v-btn>
                            </v-btn-toggle>
                            <v-select v-model="form.lineHeight" :items="lineHeights" label="Entrelinha" outlined dense hide-details style="max-width: 150px" />
                        </div>
                        <div class="doc-style-form__row">
                            <v-text-field v-model.number="form.spaceBefore" type="number" min="0" max="72" label="Espaço antes" suffix="pt" outlined dense hide-details />
                            <v-text-field v-model.number="form.spaceAfter" type="number" min="0" max="72" label="Espaço depois" suffix="pt" outlined dense hide-details />
                            <v-text-field v-model.number="form.indentLeft" type="number" min="0" max="12.5" step="0.25" label="Recuo esquerdo" suffix="cm" outlined dense hide-details />
                            <v-text-field v-model.number="form.firstLine" type="number" min="-5" max="5" step="0.25" label="Primeira linha" suffix="cm" outlined dense hide-details />
                        </div>
                    </fieldset>

                    <v-switch v-if="!isNew" v-model="form.active" inset dense hide-details label="Ativo (aparece para todos)"
                        :disabled="form.key === 'texto-normal'" class="mt-0" />
                </v-form>

                <div class="doc-style-form__preview-wrap">
                    <div class="doc-style-form__preview-label">Pré-visualização</div>
                    <div class="doc-style-form__preview">
                        <p class="doc-style-form__sample doc-style-form__sample--muted">Parágrafo anterior no texto normal do documento, para comparar espaçamentos.</p>
                        <p v-if="form.type === 'paragraph'" class="doc-style-form__sample" :style="paragraphPreview">{{ form.name || "Nome do estilo" }}: exemplo de texto com este estilo aplicado.</p>
                        <p v-else class="doc-style-form__sample">O credor <span :style="characterPreview">{{ form.name || "Trecho" }}</span> apresentou divergência no valor.</p>
                        <p class="doc-style-form__sample doc-style-form__sample--muted">Parágrafo seguinte no texto normal do documento.</p>
                    </div>
                    <p v-if="error" class="doc-style-form__error">{{ error }}</p>
                </div>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="doc-style-form__actions">
                <v-spacer></v-spacer>
                <v-btn outlined :disabled="saving" @click="close">Cancelar</v-btn>
                <v-btn color="primary" depressed :loading="saving" @click="save">Salvar estilo</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { BRAND_COLORS, FONT_FAMILIES, FONT_SIZES } from "./brand";
import { BASE_FORMAT } from "./styles/default-styles";
import { saveStyle, uniqueKey, previewCss, errorMessage } from "./styles/style-store";

const ELEMENTS = [
    { text: "Parágrafo de texto", value: "p" },
    { text: "Título 1", value: "h1" },
    { text: "Título 2", value: "h2" },
    { text: "Título 3", value: "h3" },
    { text: "Citação", value: "blockquote", disabled: true },
];

// Cadastro de um estilo do documento com pre-visualizacao ao vivo.
export default {
    name: "EditorStyleForm",
    props: {
        value: { type: Boolean, default: false },
        // Estilo a editar; null cria um novo.
        styleData: { type: Object, default: null },
    },
    data: () => ({
        form: null,
        original: {},
        saving: false,
        error: null,
        types: [{ text: "Parágrafo", value: "paragraph" }, { text: "Trecho", value: "character" }],
        elements: ELEMENTS,
        fonts: FONT_FAMILIES,
        sizes: FONT_SIZES.map((size) => ({ text: size.replace("pt", " pt"), value: size })),
        colors: BRAND_COLORS,
        lineHeights: [
            { text: "Simples (1,0)", value: "1" },
            { text: "1,15", value: "1.15" },
            { text: "1,2 (títulos)", value: "1.2" },
            { text: "1,5", value: "1.5" },
            { text: "Duplo (2,0)", value: "2" },
        ],
        aligns: [
            { value: "left", icon: "mdi-format-align-left", label: "Esquerda" },
            { value: "center", icon: "mdi-format-align-center", label: "Centro" },
            { value: "right", icon: "mdi-format-align-right", label: "Direita" },
            { value: "justify", icon: "mdi-format-align-justify", label: "Justificado" },
        ],
    }),
    computed: {
        isNew() {
            return !this.original.id && !this.original.system;
        },
        fixedQuote() {
            return !!this.form && this.form.type === "paragraph" && this.form.element === "blockquote";
        },
        marks: {
            get() {
                return ["bold", "italic", "uppercase"].filter((mark) => this.form && this.form[mark]);
            },
            set(list) {
                ["bold", "italic", "uppercase"].forEach((mark) => { this.form[mark] = list.includes(mark); });
            },
        },
        paragraphPreview() {
            const form = this.form;
            const scale = (pt) => `${Number(pt) || 0}pt`;
            return {
                ...previewCss(form),
                fontSize: form.fontSize || BASE_FORMAT.fontSize,
                lineHeight: form.lineHeight || BASE_FORMAT.lineHeight,
                marginTop: scale(form.spaceBefore),
                marginBottom: scale(form.spaceAfter),
                marginLeft: `${Number(form.indentLeft) || 0}cm`,
                textIndent: `${Number(form.firstLine) || 0}cm`,
            };
        },
        characterPreview() {
            const css = previewCss(this.form);
            return { ...css, fontSize: this.form.fontSize || "inherit", color: this.form.color || "inherit", textAlign: undefined };
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) this.reset();
            },
        },
        "form.type"(type, previous) {
            if (!previous || !this.isNew) return;
            // Trecho herda o tamanho do paragrafo; paragrafo volta ao padrao.
            if (type === "character") Object.assign(this.form, { element: null, fontSize: null, align: null });
            else Object.assign(this.form, { element: "p", fontSize: BASE_FORMAT.fontSize, align: BASE_FORMAT.align });
        },
    },
    methods: {
        reset() {
            this.original = this.styleData ? { ...this.styleData } : {};
            this.form = this.styleData
                ? { ...BASE_FORMAT, ...this.styleData, lineHeight: this.styleData.lineHeight || BASE_FORMAT.lineHeight }
                : { ...BASE_FORMAT, name: "", type: "paragraph", element: "p", active: true, system: false };
            this.error = null;
            this.saving = false;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        close() {
            this.$emit("input", false);
        },
        async save() {
            if (!this.$refs.form.validate()) return;
            const style = { ...this.form, name: this.form.name.trim() };
            if (!style.key) style.key = uniqueKey(style.name, style.id);
            this.saving = true;
            this.error = null;
            try {
                await saveStyle(style);
                this.$emit("saved", style);
                this.close();
            } catch (error) {
                this.error = errorMessage(error, "Não foi possível salvar o estilo. Tente novamente.");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
